import { SkuType } from 'types/sku';
import * as yup from 'yup';

import {
  getManufacturerDetailsValues,
  manufacturerDetailsSchema,
} from 'components/modules/SkuManufacturerDetails';

/**
 * The manufacturer id to seed the form's manufacturer select with. A SKU can
 * arrive with the joined `manufacturerDetails`, a stored id, or a populated
 * `{ _id, name }` object; the select only matches a plain id, so an object
 * would leave it blank and could be saved back as-is.
 */
const manufacturerId = (sku: any | null): string => {
  if (sku?.manufacturerDetails?._id) {
    return sku.manufacturerDetails._id;
  }

  const manufacturer = sku?.manufacturer;

  return (
    (typeof manufacturer === 'object' ? manufacturer?._id : manufacturer) || ''
  );
};

export const getInitialValues = (type: SkuType, sku: any | null) =>
  type === 'item'
    ? {
        sku: sku?._id || '',
        name: sku?.name || '',
        itemsInCase: sku?.itemsInCase || '',
        itemType: sku?.itemType || '',
        description: '',
        color: sku?.color || '',
        weight: sku?.weight || '',
        material: sku?.material || '',
        manufacturer: manufacturerId(sku),
        size: sku?.size || '',
        isSuspended: sku?.isSuspended || false,
        pricePerDelivery: Number(sku?.pricePerDelivery || '0').toFixed(2),
        pricePerReplacement: Number(sku?.pricePerReplacement || '0').toFixed(2),
        qrEnabled: sku?.qrEnabled || false,
        ...getManufacturerDetailsValues(sku ?? undefined),
      }
    : type === 'equipment'
    ? {
        name: sku?.name || '',
        category: sku?.category || '',
        manufacturer: manufacturerId(sku),
        isSuspended: sku?.isSuspended || false,
      }
    : type === 'package'
    ? {
        name: sku?.name || '',
        caseSku: sku?.caseSku || '',
        description: sku?.description || '',
        washHub: sku?.washHub || '',
        isSuspended: sku?.isSuspended || false,
        washHubsToBeAdded: [] as Array<string>,
        washHubsToBeRemoved: [] as Array<string>,
        items: sku?.items || [
          {
            sku: '',
            quantity: '',
          },
        ],
      }
    : {
        name: sku?.name || '',
        prefix: sku?.prefix || '',
        description: '',
        type,
        isSuspended: sku?.isSuspended || false,
      };

export const validationSchema = (type: SkuType) => {
  if (type === 'equipment') {
    // Category and name are mandatory, manufacturer is not (ClickUp
    // 86bbmbgfc).
    return yup.object().shape({
      category: yup.string().required('Category is required'),
      name: yup.string().required('SKU Name is required'),
      manufacturer: yup.string(),
    });
  }

  if (type === 'case') {
    return yup.object().shape({
      prefix: yup.string().required('SKU Prefix is required'),
      name: yup.string().required('SKU Name is required'),
      description: yup.string(),
    });
  }

  if (type === 'package') {
    return yup.object().shape({
      name: yup.string().required('Name is required'),
      caseSku: yup.string().required('Case SKU is required'),
      description: yup.string(),
      items: yup.array(),
    });
  }

  /**
   * Item validations
   */
  return yup.object().shape({
    sku: yup.string().required('This field is required'),
    name: yup.string().required('This field is required'),
    itemsInCase: yup.string().required('This field is required'),
    itemType: yup.string(),
    description: yup.string(),
    color: yup.string(),
    weight: yup.string(),
    material: yup.string(),
    manufacturer: yup.string(),
    size: yup.string(),
    ...manufacturerDetailsSchema,
  });
};
